import { IconCheck, IconX, IconFileText, IconArrowRight } from '@tabler/icons-react';
import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { PROTECTED_ROUTES } from '../../../../constants/routes';
import clsx from 'clsx';

interface Props {
  testId?: string
  name: string
  questions: number
  answered: number
  success: boolean
}

const TestResult: FC<Props> = ({ testId, name, questions, answered, success }) => {
  const percentage = Math.round((answered / questions) * 100);

  const content = (
    <div className={clsx(
      'flex items-center justify-between p-4 rounded-lg border-2 transition-all duration-300',
      'bg-gray-50 border-gray-200',
      'dark:bg-surface-medium dark:border-borders',
      'hover:shadow-md hover:scale-[1.01]',
      'group cursor-pointer',
      success
        ? 'hover:border-green-300 dark:hover:border-green-700'
        : 'hover:border-red-300 dark:hover:border-red-700'
    )}>
      {/* Test name */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={clsx(
          'flex-shrink-0 p-2 rounded-lg',
          success
            ? 'bg-green-100 dark:bg-green-900/30'
            : 'bg-red-100 dark:bg-red-900/30'
        )}>
          <IconFileText
            size={18}
            className={clsx(
              success
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            )}
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className={clsx(
            'font-semibold text-base mb-1 truncate transition-colors duration-300',
            'text-gray-900 dark:text-white'
          )}>
            {name}
          </h4>
          <div className="flex items-center gap-2">
            <span className={clsx(
              'text-sm font-medium transition-colors duration-300',
              success
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-700 dark:text-red-300'
            )}>
              {answered}/{questions} correctas
            </span>
          </div>
        </div>
      </div>

      {/* Result indicator */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* Percentage */}
        <div className={clsx(
          'flex items-baseline gap-1',
          success
            ? 'text-green-600 dark:text-green-400'
            : 'text-red-600 dark:text-red-400'
        )}>
          <span className="text-2xl font-bold tabular-nums">
            {percentage}
          </span>
          <span className="text-sm font-semibold">%</span>
        </div>

        {/* Status icon */}
        <div className={clsx(
          'flex-shrink-0 p-2 rounded-full',
          success
            ? 'bg-green-100 dark:bg-green-900/40'
            : 'bg-red-100 dark:bg-red-900/40'
        )}>
          {success ? (
            <IconCheck
              size={20}
              className="text-green-600 dark:text-green-400"
              strokeWidth={2.5}
            />
          ) : (
            <IconX
              size={20}
              className="text-red-600 dark:text-red-400"
              strokeWidth={2.5}
            />
          )}
        </div>
      </div>

      {/* Arrow indicator - appears on hover */}
      {testId && (
        <div className={clsx(
          'flex-shrink-0 transition-all duration-300',
          'opacity-0 group-hover:opacity-100',
          'translate-x-[-5px] group-hover:translate-x-0'
        )}>
          <IconArrowRight
            size={18}
            className={clsx(
              'text-gray-400 dark:text-gray-500',
              'group-hover:text-blue-600 dark:group-hover:text-blue-400',
              'transition-colors duration-300'
            )}
          />
        </div>
      )}
    </div>
  );

  if (testId) {
    return (
      <Link
        to={`${PROTECTED_ROUTES.TEST}?testId=${testId}`}
        className="block no-underline"
      >
        {content}
      </Link>
    );
  }

  return content;
};

export { TestResult };
